import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'doneCurrentButton',
    'doneTargetButton',
    'durationSelect',
    'elapsedLabel',
    'playButton',
    'progress',
    'progressBar',
    'stopButton',
    'timeInput'
  ]

  static values = {
    completeTone: { type: Boolean, default: true },
    storageKey: String
  }

  connect () {
    this._interval = null
    this._playedTone = false
    this._startedAt = null
    this._targetSeconds = 0
    this._handleVisibilityChange = () => this._tick()
    document.addEventListener('visibilitychange', this._handleVisibilityChange)
    this._restoreState()
  }

  disconnect () {
    document.removeEventListener('visibilitychange', this._handleVisibilityChange)
    this._stopLoop()
  }

  doneCurrent () {
    this._submit(this._elapsedSeconds())
  }

  doneTarget () {
    this._submit(this._targetSeconds)
  }

  start () {
    if (this._startedAt) return

    this._playedTone = false
    this._startedAt = Date.now()
    this._targetSeconds = this._selectedMinutes() * 60
    this._persistState()
    this._tick()
    this._startLoop()
  }

  stop () {
    this._submit(this._elapsedSeconds())
  }

  _clearState () {
    window.localStorage.removeItem(this.storageKeyValue)
  }

  _elapsedSeconds () {
    if (!this._startedAt) return 0

    return Math.max(1, Math.floor((Date.now() - this._startedAt) / 1000))
  }

  _formatElapsed (elapsedSeconds = this._elapsedSeconds()) {
    const minutes = Math.floor(elapsedSeconds / 60)
    const seconds = elapsedSeconds % 60

    if (minutes <= 0) return `${seconds}s`
    return `${minutes}m ${seconds.toString().padStart(2, '0')}s`
  }

  _persistState () {
    window.localStorage.setItem(this.storageKeyValue, JSON.stringify({
      selectedMinutes: this._selectedMinutes(),
      startedAt: this._startedAt,
      targetSeconds: this._targetSeconds,
      tonePlayed: this._playedTone
    }))
  }

  _playTone () {
    const AudioContext = window.AudioContext || window.webkitAudioContext
    if (!AudioContext) return

    const context = new AudioContext()
    const gain = context.createGain()
    const oscillator = context.createOscillator()

    oscillator.type = 'sine'
    oscillator.frequency.setValueAtTime(440, context.currentTime)
    oscillator.frequency.exponentialRampToValueAtTime(660, context.currentTime + 0.7)
    gain.gain.setValueAtTime(0.001, context.currentTime)
    gain.gain.exponentialRampToValueAtTime(0.08, context.currentTime + 0.05)
    gain.gain.exponentialRampToValueAtTime(0.001, context.currentTime + 1.2)

    oscillator.connect(gain)
    gain.connect(context.destination)
    oscillator.start()
    oscillator.stop(context.currentTime + 1.2)
  }

  _restoreState () {
    const stored = window.localStorage.getItem(this.storageKeyValue)
    if (!stored) {
      this._renderIdle()
      return
    }

    const state = JSON.parse(stored)
    this._playedTone = Boolean(state.tonePlayed)
    this._startedAt = Number(state.startedAt)
    this._targetSeconds = Number(state.targetSeconds || 0)

    if (this.hasDurationSelectTarget && Number(state.selectedMinutes || 0) > 0) {
      this.durationSelectTarget.value = String(state.selectedMinutes)
    }

    this._tick()
    this._startLoop()
  }

  _selectedMinutes () {
    if (!this.hasDurationSelectTarget) return 0

    return Number.parseInt(this.durationSelectTarget.value || '0', 10)
  }

  _startLoop () {
    this._stopLoop()
    this._interval = window.setInterval(() => this._tick(), 1000)
  }

  _stopLoop () {
    window.clearInterval(this._interval)
    this._interval = null
  }

  _submit (seconds) {
    this.timeInputTarget.value = seconds
    this._clearState()
    this._stopLoop()
    this._renderIdle()
    this.element.requestSubmit()
  }

  _tick () {
    if (!this._startedAt) {
      this._renderIdle()
      return
    }

    const elapsedSeconds = this._elapsedSeconds()
    const targetMet = this._targetSeconds > 0 && elapsedSeconds >= this._targetSeconds

    if (targetMet && !this._playedTone && this.completeToneValue) {
      this._playTone()
      this._playedTone = true
      this._persistState()
    }

    this.timeInputTarget.value = elapsedSeconds
    this.doneCurrentButtonTarget.textContent = `Done (${this._formatElapsed(elapsedSeconds)})`
    this.doneTargetButtonTarget.textContent = `Done (${this._selectedMinutes()}m)`
    this.elapsedLabelTarget.textContent = this._targetSeconds > 0 && !targetMet
      ? `${this._formatElapsed(elapsedSeconds)} / ${this._selectedMinutes()}m`
      : this._formatElapsed(elapsedSeconds)
    this.progressBarTarget.style.width = this._targetSeconds > 0
      ? `${Math.min(100, (elapsedSeconds / this._targetSeconds) * 100)}%`
      : '0%'

    this.playButtonTarget.classList.add('hidden')
    this.elapsedLabelTarget.classList.remove('hidden')
    this.stopButtonTarget.classList.toggle('hidden', targetMet)
    this.progressTarget.classList.toggle('hidden', this._targetSeconds <= 0)
    this.doneCurrentButtonTarget.classList.toggle('hidden', !targetMet)
    this.doneTargetButtonTarget.classList.toggle('hidden', !targetMet)

    if (this.hasDurationSelectTarget) {
      this.durationSelectTarget.disabled = true
      this.durationSelectTarget.classList.add('hidden')
    }
  }

  _renderIdle () {
    this._startedAt = null
    this._targetSeconds = 0
    this._playedTone = false
    this.playButtonTarget.classList.remove('hidden')
    this.elapsedLabelTarget.classList.add('hidden')
    this.stopButtonTarget.classList.add('hidden')
    this.progressTarget.classList.add('hidden')
    this.doneCurrentButtonTarget.classList.add('hidden')
    this.doneTargetButtonTarget.classList.add('hidden')
    this.progressBarTarget.style.width = '0%'

    if (this.hasDurationSelectTarget) {
      this.durationSelectTarget.disabled = false
      this.durationSelectTarget.classList.remove('hidden')
    }
  }
}
